import { StyledSelect } from "@ando/ui";
import {
  Activity,
  Brain,
  ChevronRight,
  Coins,
  FileText,
  Filter,
  Loader2,
  Wrench,
} from "lucide-react";
import * as React from "react";

import { useNavigate, useSearch } from "@tanstack/react-router";

import { Trans, useLingui } from "@lingui/react/macro";

import { Badge, Checkbox, cn } from "@ando/ui";

import type { ReasoningOutcome, ReasoningTraceListItem } from "@ando/core";

import { AdminDetailSection } from "@/features/admin/components/AdminDetailSection";
import {
  useReasoningTraceSummary,
  useReasoningTraces,
} from "@/features/admin/hooks/useAdminHypermem";
import { formatTimestamp } from "@/features/admin/lib/adminFormat";

import { HmEmpty, HmError, HmLoading } from "./HmStates";

/**
 * HyperMem Layer 3 — reasoning traces tab (Jun 28, 2026).
 *
 * Read-only viewer over ``agent_reasoning_traces``: one row per turn the
 * agent processed, with outcome classification, consolidated success
 * verdict (thumbs / action accept / response_evaluator / correction
 * patterns merged into a single column) and a per-turn drill-down that
 * joins ``llm_usage`` (cost / tokens) and ``prompt_feedback`` (explicit
 * feedback) in a single round trip.
 *
 * DRY notes:
 * - Layout / filter pattern reuses ``HmAuditTrailTab`` verbatim
 *   (filter strip + body states + row list).
 * - Drill-down is a URL-addressable in-page SECTION (``?trace=<turn>&
 *   traceUser=<user>`` wired by the hypermem route), rendered via the
 *   shared ``AdminDetailSection`` — a "place you navigate to", not a
 *   drawer. Back/forward + refresh work; the list's filter state is
 *   preserved across open/close.
 * - Hooks (``useReasoningTraces`` / ``useReasoningTraceSummary``) wrap
 *   the BE endpoints; nothing in this file talks to the API directly.
 */

const ALL = "__all__";
const LIMIT = 100;

const OUTCOMES: readonly ReasoningOutcome[] = [
  "answered",
  "proposed_action",
  "asked_clarification",
  "refused",
  "error",
] as const;

type BadgeVariant = React.ComponentProps<typeof Badge>["variant"];

function outcomeBadgeVariant(outcome: ReasoningOutcome): BadgeVariant {
  switch (outcome) {
    case "error":
    case "refused":
      return "destructive";
    case "asked_clarification":
      return "outline";
    case "proposed_action":
      return "accent";
    default:
      return "success";
  }
}

function confidenceTone(conf: number | null): string {
  if (conf === null) return "text-ink-3";
  if (conf >= 0.7) return "text-emerald-600";
  if (conf >= 0.4) return "text-amber-600";
  return "text-red-600";
}

function formatPct(value: number | null): string {
  if (value === null || Number.isNaN(value)) return "—";
  return `${(value * 100).toFixed(0)}%`;
}

function maskUserId(userId: string | null): string {
  if (!userId) return "—";
  if (userId.length <= 8) return userId;
  return `${userId.slice(0, 8)}…`;
}

function TraceRow({
  trace,
  onOpen,
}: {
  trace: ReasoningTraceListItem;
  onOpen: () => void;
}): React.ReactElement {
  return (
    <li className="rounded-xl border border-hairline bg-cream/40 p-3">
      <button type="button" onClick={onOpen} className="flex w-full items-start gap-3 text-left">
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <Badge variant={outcomeBadgeVariant(trace.outcome)}>{trace.outcome}</Badge>
            {trace.intent && (
              <Badge variant="outline" className="text-xs">
                {trace.intent}
              </Badge>
            )}
            <span className={cn("text-xs tabular-nums", confidenceTone(trace.success_confidence))}>
              <Trans>success</Trans> <strong>{formatPct(trace.success_confidence)}</strong>
            </span>
            {trace.success_signal_source && (
              <span className="text-ink-3 text-xs">({trace.success_signal_source})</span>
            )}
            <span className="ml-auto shrink-0 text-ink-3 text-xs tabular-nums">
              {formatTimestamp(trace.created_at)}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-ink-3 text-xs">
            <span className="inline-flex items-center gap-1">
              <Wrench className="h-3 w-3" aria-hidden />
              {trace.tools_count} <Trans>tool</Trans>
            </span>
            <span className="inline-flex items-center gap-1">
              <FileText className="h-3 w-3" aria-hidden />
              {trace.evidence_count} <Trans>fonti</Trans>
            </span>
            <span className="inline-flex items-center gap-1">
              <Activity className="h-3 w-3" aria-hidden />
              {trace.duration_ms ?? "—"} ms
            </span>
            <span className="font-mono text-[11px]">user {maskUserId(trace.user_id)}</span>
            <span className="font-mono text-[11px]">turn {trace.turn_id.slice(0, 12)}…</span>
          </div>
        </div>
        <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-ink-3" aria-hidden />
      </button>
    </li>
  );
}

function TraceDetailSection({
  turnId,
  userId,
  onBack,
}: {
  turnId: string;
  userId: string | null;
  onBack: () => void;
}): React.ReactElement {
  const { data, isLoading, isError } = useReasoningTraceSummary(turnId, userId, true);

  return (
    <AdminDetailSection
      onBack={onBack}
      backLabel={<Trans>Reasoning traces</Trans>}
      title={<Trans>Dettaglio turn</Trans>}
      subtitle={turnId}
    >
      <div className="space-y-5">
        {isLoading && (
          <div className="flex items-center gap-2 py-8 text-ink-3">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            <span className="text-sm">
              <Trans>Caricamento dettagli…</Trans>
            </span>
          </div>
        )}

        {isError && (
          <div className="rounded-xl border border-red-300/70 bg-transparent p-4 text-red-700 text-sm">
            <Trans>Trace non trovato o errore di rete.</Trans>
          </div>
        )}

        {data && (
          <>
            {/* Outcome + confidence */}
            <section className="space-y-2">
              <h4 className="font-semibold text-ink-1 text-sm">
                <Trans>Outcome</Trans>
              </h4>
              <div className="flex flex-wrap items-center gap-3">
                <Badge variant={outcomeBadgeVariant(data.outcome)}>{data.outcome}</Badge>
                <span className="text-ink-2 text-sm">
                  <Trans>self-confidence</Trans>{" "}
                  <strong>{formatPct(data.outcome_confidence)}</strong>
                </span>
                <span className={cn("text-sm", confidenceTone(data.success_confidence))}>
                  <Trans>success</Trans> <strong>{formatPct(data.success_confidence)}</strong>
                </span>
              </div>
              {data.success_signal_source && (
                <div className="flex flex-wrap gap-1">
                  {data.success_signal_source.split(",").map((src) => (
                    <Badge key={src} variant="secondary" className="text-xs">
                      {src.trim()}
                    </Badge>
                  ))}
                </div>
              )}
              {data.intent && (
                <p className="text-ink-3 text-xs">
                  <Trans>intent</Trans>: <code>{data.intent}</code>
                </p>
              )}
            </section>

            {/* Plan step */}
            {data.plan_step && (
              <section className="space-y-1.5">
                <h4 className="font-semibold text-ink-1 text-sm">
                  <Trans>Piano</Trans>
                </h4>
                <p className="text-ink-2 text-sm leading-relaxed">{data.plan_step}</p>
              </section>
            )}

            {/* Tools timeline — grouped by parallel_group when present
                  (Jun 28 2026: tools emitted in the same Gemini
                  ModelResponse share a ``parallel_group`` int; ≥2 tools
                  with the same group = real parallel emission). */}
            <section className="space-y-2">
              <h4 className="flex items-center gap-2 font-semibold text-ink-1 text-sm">
                <Wrench className="h-3.5 w-3.5" aria-hidden />
                <Trans>Tool chiamati</Trans> ({data.tools_called.length})
              </h4>
              {data.tools_called.length === 0 ? (
                <p className="text-ink-3 text-xs">
                  <Trans>Nessun tool firing per questo turn.</Trans>
                </p>
              ) : (
                <ul className="space-y-2">
                  {(() => {
                    // Group tools by parallel_group. Tools without the
                    // field (legacy traces) all land in group "legacy".
                    const groups = new Map<string | number, typeof data.tools_called>();
                    for (const tool of data.tools_called) {
                      const gid = (tool as { parallel_group?: number }).parallel_group ?? "legacy";
                      if (!groups.has(gid)) groups.set(gid, []);
                      groups.get(gid)!.push(tool);
                    }
                    return Array.from(groups.entries()).map(([gid, calls]) => {
                      const isParallel = calls.length >= 2 && gid !== "legacy";
                      return (
                        <li key={String(gid)} className="space-y-1">
                          {isParallel && (
                            <div className="flex items-center gap-1.5 text-[10.5px] text-ink-3 uppercase tracking-wide">
                              <span className="rounded bg-ando-orange/10 px-1.5 py-0.5 font-medium text-ando-orange">
                                <Trans>parallel</Trans> · {calls.length}
                              </span>
                              <span>
                                <Trans>same Gemini turn</Trans>
                              </span>
                            </div>
                          )}
                          <ul
                            className={cn(
                              "space-y-1.5",
                              isParallel && "rounded-lg border-ando-orange/40 border-l-2 pl-2",
                            )}
                          >
                            {calls.map((tool, idx) => (
                              <li
                                key={`${tool.name}-${idx}`}
                                className="rounded-lg border border-hairline bg-cream/40 p-2"
                              >
                                <div className="flex items-center justify-between">
                                  <code className="text-ink-1 text-xs">{tool.name}</code>
                                  <span
                                    className={cn(
                                      "text-xs tabular-nums",
                                      tool.ok ? "text-emerald-600" : "text-red-600",
                                    )}
                                  >
                                    {tool.ok ? "ok" : "fail"}
                                    {tool.duration_ms !== undefined && ` · ${tool.duration_ms}ms`}
                                  </span>
                                </div>
                                {tool.args_summary && (
                                  <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap break-words text-[11px] text-ink-3">
                                    {tool.args_summary}
                                  </pre>
                                )}
                                {tool.error && (
                                  <p className="mt-1 text-red-600 text-xs">{tool.error}</p>
                                )}
                              </li>
                            ))}
                          </ul>
                        </li>
                      );
                    });
                  })()}
                </ul>
              )}
            </section>

            {/* Evidence */}
            {data.evidence_content_ids.length > 0 && (
              <section className="space-y-2">
                <h4 className="flex items-center gap-2 font-semibold text-ink-1 text-sm">
                  <FileText className="h-3.5 w-3.5" aria-hidden />
                  <Trans>Evidence</Trans> ({data.evidence_content_ids.length})
                </h4>
                <ul className="flex flex-wrap gap-1.5">
                  {data.evidence_content_ids.map((id) => (
                    <li
                      key={id}
                      className="rounded-md border border-hairline bg-cream/40 px-2 py-0.5 font-mono text-[11px] text-ink-2"
                    >
                      {id.slice(0, 20)}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Cost / tokens */}
            <section className="space-y-2">
              <h4 className="flex items-center gap-2 font-semibold text-ink-1 text-sm">
                <Coins className="h-3.5 w-3.5" aria-hidden />
                <Trans>Costo</Trans>
              </h4>
              <div className="grid grid-cols-2 gap-y-1 text-sm">
                <span className="text-ink-3">
                  <Trans>LLM calls</Trans>
                </span>
                <span className="text-right font-mono text-ink-1">{data.tokens.llm_calls}</span>
                <span className="text-ink-3">
                  <Trans>Token in / out</Trans>
                </span>
                <span className="text-right font-mono text-ink-1">
                  {data.tokens.input} / {data.tokens.output}
                </span>
                <span className="text-ink-3">
                  <Trans>Cost (USD)</Trans>
                </span>
                <span className="text-right font-mono text-ink-1">${data.cost_usd.toFixed(5)}</span>
                <span className="text-ink-3">
                  <Trans>Wall-clock</Trans>
                </span>
                <span className="text-right font-mono text-ink-1">
                  {data.duration_ms ?? "—"} ms
                </span>
              </div>
            </section>

            {/* Explicit user feedback */}
            {data.user_feedback && (
              <section className="space-y-1.5">
                <h4 className="font-semibold text-ink-1 text-sm">
                  <Trans>Feedback utente</Trans>
                </h4>
                <Badge variant="outline" className="text-xs">
                  {data.user_feedback.type} ({data.user_feedback.value ?? "—"})
                </Badge>
              </section>
            )}

            {/* Assumptions */}
            {Object.keys(data.assumptions).length > 0 && (
              <section className="space-y-1.5">
                <h4 className="font-semibold text-ink-1 text-sm">
                  <Trans>Assunzioni</Trans>
                </h4>
                <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-hairline bg-cream/60 p-2 text-[11px] text-ink-2">
                  {JSON.stringify(data.assumptions, null, 2)}
                </pre>
              </section>
            )}
          </>
        )}
      </div>
    </AdminDetailSection>
  );
}

export function HmReasoningTracesTab({
  tenantId,
}: {
  tenantId: string;
}): React.ReactElement {
  const { t } = useLingui();
  const navigate = useNavigate();
  // Detail is a URL-addressable in-page SECTION (``?trace=<turn>&traceUser=<user>``)
  // wired by the hypermem route — a place you navigate to, not a drawer.
  const search = useSearch({ from: "/_authed/_modal/admin/hypermem" });
  const detailTurnId = search.trace;
  const detailUserId = search.traceUser;
  const [outcomeFilter, setOutcomeFilter] = React.useState<string>(ALL);
  const [userIdInput, setUserIdInput] = React.useState("");
  const [appliedUserId, setAppliedUserId] = React.useState("");
  const [lowOnly, setLowOnly] = React.useState(false);
  const openTrace = React.useCallback(
    (turnId: string, userId: string | null) => {
      void navigate({
        to: "/admin/hypermem",
        search: { ...search, trace: turnId, traceUser: userId ?? undefined },
      });
    },
    [navigate, search],
  );
  const closeTrace = React.useCallback(() => {
    void navigate({
      to: "/admin/hypermem",
      search: { ...search, trace: undefined, traceUser: undefined },
    });
  }, [navigate, search]);

  const args = React.useMemo(
    () => ({
      limit: LIMIT,
      ...(outcomeFilter !== ALL ? { outcome: outcomeFilter as ReasoningOutcome } : {}),
      ...(appliedUserId.trim() ? { user_id: appliedUserId.trim() } : {}),
      ...(lowOnly ? { max_success_confidence: 0.4 } : {}),
    }),
    [outcomeFilter, appliedUserId, lowOnly],
  );

  const { data, isLoading, isError, refetch } = useReasoningTraces(tenantId, args);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const filtersActive = outcomeFilter !== ALL || !!appliedUserId || lowOnly;

  const applyUserFilter = () => setAppliedUserId(userIdInput);
  const clearFilters = () => {
    setOutcomeFilter(ALL);
    setUserIdInput("");
    setAppliedUserId("");
    setLowOnly(false);
  };

  // Detail takeover — replaces the list with the full-width trace section
  // (back returns to the same filtered list; local filter state is preserved).
  if (detailTurnId) {
    return (
      <TraceDetailSection turnId={detailTurnId} userId={detailUserId ?? null} onBack={closeTrace} />
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h3 className="flex items-center gap-2 font-semibold text-ink-1 text-sm">
          <Brain className="h-4 w-4 text-ando-orange" aria-hidden />
          <Trans>Reasoning traces (per-turn)</Trans>
        </h3>
        <p className="mt-1 text-ink-3 text-xs">
          <Trans>
            Un record per ogni turn dell'agente. ``success_confidence`` consolida thumbs, action
            accept/reject, response evaluator e correction patterns in un'unica metrica leggibile da
            MetaPrompt. Filtra "Solo bassa qualità" per il workflow di QA.
          </Trans>
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label htmlFor="hm-reasoning-outcome" className="block text-ink-2 text-xs">
            <Trans>Outcome</Trans>
          </label>
          <StyledSelect
            id="hm-reasoning-outcome"
            value={outcomeFilter}
            onChange={(e) => setOutcomeFilter(e.target.value)}
            className="h-9 w-[220px] rounded-lg border border-hairline bg-cream/40 px-3 text-ink-1 text-sm focus:outline-none focus:ring-2 focus:ring-ando-orange/40"
          >
            <option value={ALL}>{t`Tutti`}</option>
            {OUTCOMES.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </StyledSelect>
        </div>

        <div className="space-y-1">
          <label htmlFor="hm-reasoning-user" className="block text-ink-2 text-xs">
            <Trans>User ID (opzionale)</Trans>
          </label>
          <div className="flex gap-2">
            <input
              id="hm-reasoning-user"
              value={userIdInput}
              onChange={(e) => setUserIdInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyUserFilter();
              }}
              placeholder={t`UUID o vuoto`}
              className="h-9 w-[260px] rounded-lg border border-hairline bg-cream/40 px-3 text-ink-1 text-sm placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-ando-orange/40"
            />
            <button
              type="button"
              onClick={applyUserFilter}
              className="flex h-9 items-center gap-1.5 rounded-lg border border-hairline px-3 font-medium text-ink-2 text-sm transition-colors hover:bg-ink-3/10"
            >
              <Filter className="h-3.5 w-3.5" aria-hidden />
              <Trans>Filtra</Trans>
            </button>
          </div>
        </div>

        <label className="flex h-9 cursor-pointer items-center gap-2 self-end rounded-lg border border-hairline px-3 text-ink-2 text-sm hover:bg-ink-3/10">
          <Checkbox checked={lowOnly} onChange={(e) => setLowOnly(e.target.checked)} />
          <Trans>Solo bassa qualità (≤ 40%)</Trans>
        </label>

        {filtersActive && (
          <button
            type="button"
            onClick={clearFilters}
            className="h-9 self-end rounded-lg px-3 font-medium text-ink-2 text-sm transition-colors hover:bg-ink-3/10"
          >
            <Trans>Reset</Trans>
          </button>
        )}
      </div>

      {/* Body */}
      {isLoading && <HmLoading label={<Trans>Caricamento traces…</Trans>} />}

      {isError && (
        <HmError
          message={<Trans>Impossibile caricare i reasoning traces.</Trans>}
          onRetry={() => refetch()}
        />
      )}

      {!isLoading && !isError && items.length === 0 && (
        <HmEmpty
          icon={<Brain className="h-8 w-8 opacity-50" aria-hidden />}
          label={<Trans>Nessun trace per questi filtri.</Trans>}
        />
      )}

      {!isLoading && !isError && items.length > 0 && (
        <>
          <p className="text-ink-3 text-xs">
            <Trans>
              {items.length} di {total} mostrati (limit {LIMIT})
            </Trans>
          </p>
          <ul className={cn("space-y-2")}>
            {items.map((trace) => (
              <TraceRow
                key={trace.id}
                trace={trace}
                onOpen={() => openTrace(trace.turn_id, trace.user_id)}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
